import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  AlertTriangle,
  ArrowDownCircle,
  Calendar,
  CreditCard,
  FileText,
  PieChart,
  Users,
  Wallet,
} from "lucide-react"
import { formatDateLong, formatDateWithTime, formatMoney } from "../../utils/format"
import { getGroupTheme } from "../../utils/groupTheme"
import api from "../../utils/api"

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toLabelCase = (value) => {
  const text = String(value || "").trim()
  if (!text) return ""
  return text
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ")
}

const resolveEventLabel = (group) => {
  const explicit = String(group?.useCase || "").trim()
  if (explicit && explicit.toLowerCase() !== "generic-non-recurring") return explicit

  const typed = toLabelCase(group?.type)
  if (typed) return typed

  return "Event Contribution"
}

const getStatusMeta = (status) => {
  const normalized = String(status || "").trim().toLowerCase()
  if (normalized === "active") {
    return {
      label: "Active",
      className: "border-[#1F7DFF] bg-[#1F7DFF]/60 text-sky-900",
      dotClassName: "bg-[#1F7DFF]",
    }
  }
  if (normalized === "planning") {
    return {
      label: "Planning",
      className: "border-amber-200 bg-amber-50 text-amber-700",
      dotClassName: "bg-amber-500",
    }
  }
  if (normalized === "closed") {
    return {
      label: "Closed",
      className: "border-slate-200 bg-slate-100 text-slate-700",
      dotClassName: "bg-slate-500",
    }
  }
  return {
    label: "Status",
    className: "border-gray-200 bg-gray-50 text-gray-600",
    dotClassName: "bg-gray-400",
  }
}

function HeaderStat({ icon: Icon, label, value, theme, className = "" }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className={`flex h-8 w-8 items-center justify-center rounded-xl ${theme.bg} ${theme.text} shadow-sm`}>
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-gray-500 leading-tight">{label}</p>
        <p className="text-sm font-semibold leading-tight text-slate-900 break-words">{value}</p>
      </div>
    </div>
  )
}

function HighlightCard({ icon: Icon, label, value, tone = "neutral", helper = "", theme }) {
  const valueClassName =
    tone === "success"
      ? "text-sky-900"
      : tone === "warning"
        ? "text-amber-600"
        : "text-slate-900"

  return (
    <div className="bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center">
      <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1`}>
        <Icon size={16} />
      </div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight">{label}</p>
      <p className={`text-xs font-bold mt-0.5 ${valueClassName}`}>{value}</p>
      <p className="mt-0.5 text-[10px] text-gray-500 hidden md:block">{helper}</p>
    </div>
  )
}

function GroupNonRecurringPlaceholderPanel({
  group,
  onContribute,
  isSubmitting = false,
  readOnly = false,
}) {
  const theme = getGroupTheme(group)
  const [isContributionModalOpen, setIsContributionModalOpen] = useState(false)
  const [contributionMemberId, setContributionMemberId] = useState("")
  const [contributionAmountInput, setContributionAmountInput] = useState("")
  const [contributionError, setContributionError] = useState("")
  const [contributionSuccess, setContributionSuccess] = useState("")
  const [isLoadingWalletBalance, setIsLoadingWalletBalance] = useState(false)
  const [myWalletBalance, setMyWalletBalance] = useState(0)
  const [isLoadingRecentContributions, setIsLoadingRecentContributions] = useState(false)
  const [recentContributions, setRecentContributions] = useState([])
  const members = useMemo(
    () => (Array.isArray(group?.members) ? group.members : []),
    [group?.members]
  )
  const membersCount = Number(group?.membersCount || 0) || members.length
  const statusMeta = getStatusMeta(group?.status)
  const shouldShowStatusPill = String(statusMeta?.label || "").toLowerCase() !== "planning"
  const eventLabel = resolveEventLabel(group)

  const myMemberEntry = useMemo(() => {
    const myMemberId = String(group?.myMemberId || "")
    if (!myMemberId) return null
    return members.find((member) => String(member?.id || "") === myMemberId) || null
  }, [group?.myMemberId, members])

  const perPersonAmount = toNumber(group?.amountPerPerson)
  const totalExpected = toNumber(group?.totalBudget)
  const totalRaised = toNumber(group?.groupWalletBalance)
  const myContributions = toNumber(myMemberEntry?.paid)
  const myExpected = toNumber(myMemberEntry?.expected ?? perPersonAmount)
  const myOutstanding = Math.max(
    0,
    toNumber(myMemberEntry?.dueAmount ?? myExpected - myContributions)
  )
  const isMyGoalReached = myExpected > 0 && myContributions >= myExpected
  const progressPct =
    totalExpected > 0 ? Math.min(100, Math.round((totalRaised / totalExpected) * 100)) : 0
  const eventDateLabel = group?.eventDate ? formatDateLong(group.eventDate) : "TBC"
  const explainer =
    String(group?.shortEventExplainer || "").trim() ||
    "Track voluntary contributions and progress toward this group goal."
  const selectedMemberEntry = useMemo(() => {
    if (!contributionMemberId) return null
    return members.find((member) => String(member?.id || "") === String(contributionMemberId)) || null
  }, [contributionMemberId, members])
  const selectedMemberName = selectedMemberEntry?.name || "Member"
  const selectedMemberContributed = toNumber(selectedMemberEntry?.paid)
  const selectedMemberOutstanding = Math.max(0, toNumber(selectedMemberEntry?.dueAmount))
  const selectedMemberExpected = toNumber(selectedMemberEntry?.expected ?? perPersonAmount)
  const isSelectedMemberGoalReached =
    selectedMemberExpected > 0 && selectedMemberContributed >= selectedMemberExpected
  const isPayingForAnotherMember =
    !!contributionMemberId && String(contributionMemberId) !== String(group?.myMemberId || "")
  const contributionAmountValue = Number(contributionAmountInput)
  const contributionAmount =
    Number.isFinite(contributionAmountValue) && contributionAmountValue > 0
      ? contributionAmountValue
      : 0
  const isContributionAmountValid =
    Number.isFinite(contributionAmountValue) && contributionAmountValue > 0
  const outstandingAfterPayment = Math.max(0, selectedMemberOutstanding - contributionAmount)

  const loadRecentContributions = useCallback(async (showLoader = true) => {
    if (!group?.id) {
      setRecentContributions([])
      return
    }
    if (showLoader) setIsLoadingRecentContributions(true)
    try {
      const res = await api.get(`/groups/${group.id}/admin/ledger`)
      const txRows = Array.isArray(res?.data?.walletTransactions) ? res.data.walletTransactions : []
      const rows = txRows
        .filter(
          (row) =>
            String(row?.type || "").toLowerCase() === "member_contribution" && toNumber(row?.amount) > 0
        )
        .sort((a, b) => new Date(b?.timestamp || 0).getTime() - new Date(a?.timestamp || 0).getTime())
        .slice(0, 5)
        .map((row) => ({
          id: row?.id || `${row?.timestamp || ""}-${row?.memberId || ""}`,
          memberName: String(row?.memberName || "").trim() || "Member",
          amount: toNumber(row?.amount),
          timestamp: row?.timestamp || null,
        }))
      setRecentContributions(rows)
    } catch {
      setRecentContributions([])
    } finally {
      if (showLoader) setIsLoadingRecentContributions(false)
    }
  }, [group?.id])

  useEffect(() => {
    const defaultMemberId = String(group?.myMemberId || members?.[0]?.id || "")
    setContributionMemberId(defaultMemberId)
  }, [group?.myMemberId, members])

  useEffect(() => {
    let isMounted = true
    const loadWalletBalance = async () => {
      if (!group?.id) {
        if (isMounted) setMyWalletBalance(0)
        return
      }
      setIsLoadingWalletBalance(true)
      try {
        const res = await api.get(`/groups/${group.id}/transactions/my`)
        if (!isMounted) return
        setMyWalletBalance(toNumber(res?.data?.walletBalance || 0))
      } catch {
        if (isMounted) setMyWalletBalance(0)
      } finally {
        if (isMounted) setIsLoadingWalletBalance(false)
      }
    }
    loadWalletBalance()
    return () => {
      isMounted = false
    }
  }, [group?.id])

  useEffect(() => {
    loadRecentContributions(true)
  }, [loadRecentContributions])

  const openContributionModal = async () => {
    if (readOnly) return
    setContributionError("")
    setContributionSuccess("")
    if (group?.id) {
      setIsLoadingWalletBalance(true)
      try {
        const res = await api.get(`/groups/${group.id}/transactions/my`)
        setMyWalletBalance(toNumber(res?.data?.walletBalance || 0))
      } catch {
        // Keep existing displayed value when wallet refresh fails.
      } finally {
        setIsLoadingWalletBalance(false)
      }
    }
    const fallbackMemberId = String(group?.myMemberId || members?.[0]?.id || "")
    const memberId = String(contributionMemberId || fallbackMemberId || "")
    const memberEntry = members.find((member) => String(member?.id || "") === memberId) || null
    const suggestedAmount = Math.max(0, toNumber(memberEntry?.dueAmount))
    setContributionMemberId(memberId)
    setContributionAmountInput(
      suggestedAmount > 0 ? String(suggestedAmount) : String(Math.max(0, perPersonAmount))
    )
    setIsContributionModalOpen(true)
  }

  const closeContributionModal = () => {
    if (isSubmitting) return
    setIsContributionModalOpen(false)
  }

  const handleQuickPayMyOutstanding = () => {
    const myMemberId = String(group?.myMemberId || "")
    if (!myMemberId) return
    const entry = members.find((member) => String(member?.id || "") === myMemberId) || null
    const dueAmount = Math.max(0, toNumber(entry?.dueAmount))
    setContributionMemberId(myMemberId)
    setContributionAmountInput(dueAmount > 0 ? String(dueAmount) : "")
  }

  const handleQuickPaySelectedFull = () => {
    const dueAmount = Math.max(0, selectedMemberOutstanding)
    setContributionAmountInput(dueAmount > 0 ? String(dueAmount) : "")
  }

  const handleSubmitContribution = async () => {
    if (!group?.id || !contributionMemberId || isSubmitting) return
    setContributionError("")
    setContributionSuccess("")
    if (!isContributionAmountValid) {
      setContributionError("Amount must be greater than 0.")
      return
    }
    const result = await onContribute?.({
      memberId: contributionMemberId,
      amount: contributionAmount,
    })
    if (!result?.ok) {
      setContributionError(result?.message || "Failed to contribute.")
      return
    }
    if (Number.isFinite(Number(result?.memberWalletBalance))) {
      setMyWalletBalance(Number(result.memberWalletBalance))
    }
    setContributionSuccess(result?.message || "Contribution posted.")
    setIsContributionModalOpen(false)
    setContributionAmountInput("")
    await loadRecentContributions(false)
  }

  return (
    <div className="space-y-3">
      <section className="rounded-[26px] border border-gray-200 bg-white/95 p-3 shadow-sm sm:p-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-2.5">
              <div className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-[18px] ${theme.bg} ${theme.text} shadow-inner`}>
                <CreditCard size={24} />
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-semibold ${theme.text}`}>{eventLabel}</p>
                <h2 className="mt-0.5 break-words text-lg font-bold leading-tight tracking-tight text-slate-950 sm:text-2xl">
                  {group?.name || "Unnamed Group"}
                </h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  Group ID: <span className="font-semibold text-gray-700">{group?.groupCode || "-"}</span>
                </p>
                <p className="mt-1 max-w-2xl text-[11px] leading-4 text-gray-500 hidden sm:block">{explainer}</p>
              </div>
            </div>

            {shouldShowStatusPill && (
              <div className={`inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-sm ${statusMeta.className}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${statusMeta.dotClassName}`} />
                {statusMeta.label}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 border-t border-gray-100 pt-3 md:grid-cols-3">
            <HeaderStat
              icon={Calendar}
              label="Event Date"
              value={eventDateLabel}
              theme={theme}
            />
            <HeaderStat
              icon={Users}
              label="Members"
              value={membersCount}
              theme={theme}
            />
            <HeaderStat
              icon={CreditCard}
              label="Per Person Amount"
              value={perPersonAmount > 0 ? formatMoney(perPersonAmount) : "TBC"}
              theme={theme}
              className="col-span-2 md:col-span-1"
            />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-2 xl:grid-cols-[0.95fr_1.05fr_1.1fr]">
        <HighlightCard
          icon={Wallet}
          label="My Wallet Balance"
          value={isLoadingWalletBalance ? "Loading..." : formatMoney(myWalletBalance)}
          helper="Available balance in your Groups Wallet."
          tone="success"
          theme={theme}
        />

        <div className="bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center">
          <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1`}>
            <PieChart size={16} />
          </div>
          <p className="text-[10px] font-semibold uppercase tracking-tight text-gray-500">Total Raised</p>
          <p className="text-xs font-bold mt-0.5 text-slate-900">{formatMoney(totalRaised)}</p>
          <p className="mt-0.5 text-[10px] text-gray-500">
            of <span className="font-semibold text-gray-700">{formatMoney(totalExpected)}</span>
          </p>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${theme.progressFill} transition-all duration-500`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-1 text-[10px] font-semibold text-sky-900">{progressPct}%</div>
        </div>

        <div className="col-span-2 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 p-2.5 text-white shadow-sm xl:col-span-1">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/12">
              <CreditCard size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight tracking-tight">Contribute Now</p>
              <p className="mt-0.5 text-[10px] leading-4 text-white/90">
                Contribute for yourself or any member using your Groups Wallet balance.
              </p>
            </div>
            <button
              type="button"
              onClick={openContributionModal}
              disabled={readOnly || isSubmitting}
              className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[10px] font-semibold text-white/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "..." : "Contribute"}
            </button>
          </div>
        </div>
      </section>

      {!!contributionError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
          {contributionError}
        </div>
      )}
      {!!contributionSuccess && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-700">
          {contributionSuccess}
        </div>
      )}

      <section className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl border border-gray-200 bg-white/95 p-3 shadow-sm">
          <div className="flex items-start gap-2">
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${theme.bg} ${theme.text}`}>
              <ArrowDownCircle size={15} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500">My Contributions</p>
              <p className="mt-0.5 text-lg font-semibold leading-tight text-slate-950">{formatMoney(myContributions)}</p>
              {isMyGoalReached && (
                <span className="mt-1 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                  Goal reached
                </span>
              )}
              <p className="mt-0.5 text-[11px] text-gray-500 hidden sm:block">Total amount you have contributed so far.</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-orange-100 bg-white/95 p-3 shadow-sm">
          <div className="flex items-start gap-2">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-500">
              <AlertTriangle size={15} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500">Outstanding Amount</p>
              <p className="mt-0.5 text-lg font-semibold leading-tight text-orange-500">{formatMoney(myOutstanding)}</p>
              <p className="mt-0.5 text-[11px] text-gray-500 hidden sm:block">Your remaining amount for this event.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[26px] border border-gray-200 bg-white/95 p-3 shadow-sm sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`flex h-8 w-8 items-center justify-center rounded-xl ${theme.bg} ${theme.text}`}>
              <FileText size={14} />
            </div>
            <h3 className="text-base font-semibold tracking-tight text-slate-950">Recent Contributions</h3>
          </div>
          <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold ${theme.text}`}>
            Last 5
          </span>
        </div>

        <div className="mt-2.5 rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-3">
          {isLoadingRecentContributions ? (
            <p className="text-[11px] text-gray-500">Loading recent contributions...</p>
          ) : recentContributions.length === 0 ? (
            <div className="flex items-center gap-2.5 py-1.5 text-left">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <FileText size={18} />
              </div>
              <div>
                <p className="text-sm font-semibold tracking-tight text-slate-900">No contributions yet</p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  Recent contributions will show here.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {recentContributions.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-slate-900">{row.memberName}</p>
                    <p className="text-[10px] text-gray-500">
                      {row.timestamp ? formatDateWithTime(row.timestamp) : "Just now"}
                    </p>
                  </div>
                  <p className="shrink-0 text-xs font-semibold text-emerald-700">
                    +{formatMoney(row.amount)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {isContributionModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-xl">
            <h4 className="text-sm font-semibold text-gray-900">Contribute Now</h4>
            <p className="mt-1 text-xs text-gray-600">
              Choose who this contribution should count for and enter amount.
            </p>

            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
                  Member
                </label>
                <select
                  value={contributionMemberId}
                  onChange={(event) => setContributionMemberId(event.target.value)}
                  className={`w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs outline-none focus:ring-2 ${theme.ring}`}
                >
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name || "Member"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Outstanding</p>
                  <p className="mt-0.5 text-xs font-semibold text-orange-600">
                    {formatMoney(selectedMemberOutstanding)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">My Wallet</p>
                  <p className={`mt-0.5 text-xs font-semibold ${theme.text}`}>
                    {formatMoney(myWalletBalance)}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-gray-500">Member Progress</p>
                <p className="mt-0.5 text-xs text-gray-700">
                  {selectedMemberName}: {formatMoney(selectedMemberContributed)} contributed of{" "}
                  {formatMoney(selectedMemberExpected)}
                </p>
                {isSelectedMemberGoalReached && (
                  <span className="mt-1 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    Goal reached
                  </span>
                )}
              </div>

              {isPayingForAnotherMember && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
                  You are paying for another member. This amount is deducted from your wallet.
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleQuickPayMyOutstanding}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Pay My Outstanding
                </button>
                <button
                  type="button"
                  onClick={handleQuickPaySelectedFull}
                  disabled={selectedMemberOutstanding <= 0}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Pay Full for selected member
                </button>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={contributionAmountInput}
                  onChange={(event) => setContributionAmountInput(event.target.value)}
                  className={`w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs outline-none focus:ring-2 ${theme.ring}`}
                />
                <p className="mt-1 text-[10px] text-gray-500">
                  After this payment, Outstanding will be{" "}
                  <span className="font-semibold text-gray-700">{formatMoney(outstandingAfterPayment)}</span>.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeContributionModal}
                disabled={isSubmitting}
                className="rounded-lg border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitContribution}
                disabled={isSubmitting || !isContributionAmountValid}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 ${theme.button}`}
              >
                {isSubmitting ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupNonRecurringPlaceholderPanel
